export type StudyAnswer = {
    id: string;
    answerId: string;
    missionId: string;
    volunteerId: string | null;
    questionId: string;
    questionType: string;
    answer: any;
};

export type StudyQuestionStats = {
    questionType: string;
    answerRows: number;
    touchedPeople: number;
    stats?: any;
};

export type StudyVolunteerStats = {
    submissions: number;
    touchedPeople: number;
    mascotHugs: number;
};

export type StudyStatistics = {
    perQuestion: Map<string, StudyQuestionStats>;
    perVolunteer: Map<string, StudyVolunteerStats>;
};

// Returns the touched people count from a 'count' or 'mascot-hug-count' row
export function getTouchedPeople(answer: StudyAnswer): number {
    if (
        answer.questionId !== 'count' &&
        answer.questionId !== 'mascot-hug-count'
    )
        return 0;
    const payload = answer.answer;
    if (typeof payload === 'number') return Math.max(0, Math.trunc(payload));
    if (payload && typeof payload === 'object' && 'value' in payload) {
        const value = (payload as { value: unknown }).value;
        if (typeof value === 'number') return Math.max(0, Math.trunc(value));
    }
    return 0;
}

export function computeStudyStatistics(
    answers: StudyAnswer[]
): StudyStatistics {
    // Group answers by answerId and find the count for each group
    const countsPerAnswerId = new Map<string, number>();
    for (const answer of answers) {
        const count = getTouchedPeople(answer);
        if (count > 0) {
            countsPerAnswerId.set(answer.answerId, count);
        }
    }

    const perQuestion = new Map<string, StudyQuestionStats>();
    const perVolunteer = new Map<string, StudyVolunteerStats>();

    for (const answer of answers) {
        const qId = answer.questionId;
        const qType = answer.questionType;
        const payload = answer.answer;

        // --- Per Question Stats ---
        if (qId !== 'count' && qId !== 'mascot-hug-count') {
            const questionStats = perQuestion.get(qId) ?? {
                questionType: qType,
                answerRows: 0,
                touchedPeople: 0,
                stats:
                    qType === 'TWO_CHOICES'
                        ? { choice1: 0, choice2: 0 }
                        : qType === 'YES_NO'
                          ? { yes: 0, no: 0, subYes: 0, subNo: 0 }
                          : qType === 'IMAGE_GALLERY'
                            ? { images: new Map<string, number>() }
                            : undefined,
            };

            questionStats.answerRows += 1;
            const setTouchedCount = countsPerAnswerId.get(answer.answerId) ?? 0;
            questionStats.touchedPeople += setTouchedCount;

            // Update type-specific stats
            if (
                qType === 'TWO_CHOICES' &&
                payload &&
                typeof payload === 'object'
            ) {
                questionStats.stats.choice1 += Number(payload.choice1 || 0);
                questionStats.stats.choice2 += Number(payload.choice2 || 0);
            } else if (
                qType === 'YES_NO' &&
                payload &&
                typeof payload === 'object'
            ) {
                if (payload.yes === true) questionStats.stats.yes += 1;
                else if (payload.yes === false) questionStats.stats.no += 1;

                if (payload.subYes === true) questionStats.stats.subYes += 1;
                else if (payload.subYes === false)
                    questionStats.stats.subNo += 1;
            } else if (
                qType === 'IMAGE_GALLERY' &&
                payload &&
                typeof payload === 'object' &&
                Array.isArray(payload.selection)
            ) {
                for (const imgKey of payload.selection as string[]) {
                    questionStats.stats.images.set(
                        imgKey,
                        (questionStats.stats.images.get(imgKey) ?? 0) + 1
                    );
                }
            }

            perQuestion.set(qId, questionStats);
        }

        // --- Per Volunteer Stats ---
        if (answer.volunteerId) {
            const volunteerStats = perVolunteer.get(answer.volunteerId) ?? {
                submissions: 0,
                touchedPeople: 0,
                mascotHugs: 0,
            };

            if (qId === 'count') {
                volunteerStats.submissions += 1;
                volunteerStats.touchedPeople += getTouchedPeople(answer);
            } else if (qId === 'mascot-hug-count') {
                volunteerStats.mascotHugs += getTouchedPeople(answer);
            }
            perVolunteer.set(answer.volunteerId, volunteerStats);
        }
    }

    return { perQuestion, perVolunteer };
}
